import { Repeat, TrendingUp, Coins, Gem, ReceiptIndianRupee, Sunset, CheckCircle2 } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const PRODUCTS = [
  {
    icon: Repeat,
    title: "Systematic Investment Plans (SIP)",
    desc: "Disciplined monthly investing that turns income into a compounding corpus — mapped to goals, reviewed every year.",
  },
  {
    icon: TrendingUp,
    title: "Mutual Funds",
    desc: "Actively managed, research-backed fund portfolios across equity, hybrid and debt — matched to your risk profile.",
  },
  {
    icon: Coins,
    title: "Digital Gold Investment Plans",
    desc: "24K gold in fractional, fully-secured digital form — hedge and heirloom without locker worries.",
  },
  {
    icon: Gem,
    title: "Portfolio Management Services (PMS)",
    desc: "Discretionary, professionally managed portfolios for substantial wealth — with institutional-grade reporting.",
  },
  {
    icon: ReceiptIndianRupee,
    title: "Tax Saving Investments (ELSS)",
    desc: "Equity-linked savings that reduce your tax outgo under 80C while keeping your money working for growth.",
  },
  {
    icon: Sunset,
    title: "Retirement Planning",
    desc: "Corpus-building and income mapping for the decade you stop earning — so your lifestyle never has to.",
  },
];

const WEALTH_SERVICES = ["SIP", "Mutual Funds", "Digital Gold", "PMS", "ELSS / Tax Saving", "Retirement Planning"];

export default function Wealth() {
  return (
    <div data-testid="wealth-page">
      <PageHero
        eyebrow="Private Wealth Advisory"
        title={["Wealth,", "engineered."]}
        sub="Long-term wealth is not chased — it is structured. Our wealth desk builds portfolios with discipline, reviews them with rigour, and reports to you in plain language."
      >
        <ScrollCta href="#consult" label="Consult a Wealth Expert" testId="wealth-consult-btn" />
      </PageHero>

      {/* PRODUCTS */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="wealth-products-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">The Suite</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Six instruments. One long-term strategy.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="card-lift h-full rounded-3xl border border-slate-200 bg-white p-7" data-testid={`wealth-card-${i + 1}`}>
                  <div className="flex items-center justify-between">
                    <p.icon className="h-8 w-8 text-amber-600" />
                    <span className="font-mono text-xs font-semibold text-slate-400">0{i + 1}</span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-extrabold text-navy">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="grain relative bg-navy py-20 sm:py-28" data-testid="wealth-philosophy-section">
        <div className="container-x relative z-10 text-center">
          <Reveal>
            <p className="eyebrow !text-gold">Our Philosophy</p>
            <p className="mx-auto mt-6 max-w-3xl font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl">
              "Wealth is built in decades, not quarters — and protected long before it is needed."
            </p>
            <p className="mx-auto mt-6 max-w-xl text-white/65">
              Every portfolio we run follows the same discipline: goal first, product second, review always.
            </p>
          </Reveal>
          <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3">
            {["Goal-first structuring", "Annual portfolio reviews", "Plain-language reporting"].map((item, i) => (
              <Reveal key={item} delay={i * 0.1}>
                <div className="flex items-center justify-center gap-2.5 rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-sm font-bold text-white">
                  <CheckCircle2 className="h-4 w-4 text-gold" /> {item}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULT */}
      <section id="consult" className="py-16 sm:py-24" data-testid="wealth-consult-section">
        <div className="container-x grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Private Desk</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Consult a Wealth Expert.
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              A no-obligation first conversation: where you stand, where you want to be, and the shortest honest path
              between the two. Investments in securities are subject to market risks.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" data-testid="wealth-consult-form-card">
              <LeadForm
                source="wealth-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={WEALTH_SERVICES}
                cta="Consult a Wealth Expert"
                idPrefix="wealth-consult"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
